import React from 'react';
import { injectIntl } from 'react-intl';
import { parseClasses } from '../../lib/ep.js';

import { SquaredButton } from './ActionButton';
import FieldLabel from './FieldLabel';
import Tooltip from './Tooltip';

const Selector = ({ position, tooltip, change, value, ...props }) => {
	const only_center_edges = props?.only_center_edges ?? false;

	const formatShortValue = (v) => {
		if(v.length < 3) return v;

		let v_comp;
		let h_comp;

		if(['top', 'left', 'bottom', 'right'].includes(v)) return v;

		const v_parts = v.split(" ");

		if(v_parts[0] == 'top') v_comp = 't';
		if(v_parts[0] == 'center') v_comp = 'c';
		if(v_parts[0] == 'bottom') v_comp = 'b';

		if(v_parts[1] == 'left') h_comp = 'l';
		if(v_parts[1] == 'center') h_comp = 'c';
		if(v_parts[1] == 'right') h_comp = 'r';

		return `${v_comp}${h_comp}`;
	};

	value = formatShortValue(value);

	let classes = [];
	if(value == position) classes.push('selected');
	if(only_center_edges) classes.push('invisible');

	return (
		<a class={classes} onClick={() => change(position)}>
			<Tooltip
				with_icon={false}
				position={tooltip?.position ?? 'top'}
			>{tooltip?.children ?? tooltip}</Tooltip>
		</a>
	);
};

const CustomSelector = ({ item, change, value, ...props }) => {
	let classname = [];
	if(value == item.value) classname.push('selected');

	let button_props = { other:classname.join(" ") };
	if(item.circled != undefined && item.circled) button_props.circled = true;

	return (
		<SquaredButton
			color={item.color}
			tooltip={<Tooltip with_icon={false} position={item.tooltip?.position ?? 'top'}>{item.tooltip?.children ?? item.tooltip}</Tooltip>}
			icon={item.icon}
			_class={button_props}
			onClick={() => change(item.value)}
			aria-label="Change"
		/>
	);
};

const TextWeightSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'p':
			icon = 'knk-tag-paragraph';
			break;

		case 'b':
			icon = 'knk-tag-bold';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const DirectionMathSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'less':
			icon = 'knk-minus';
			break;

		case 'more':
			icon = 'knk-plus';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const PhotoOrientationSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'single':
			icon = 'knk-one-column';
			break;

		case 'double':
			icon = 'knk-two-column';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const RatingIcon = ({ position, change, value, ...props }) => {
	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			color='black'
			icon={position}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const TextSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'cl':
		case 'left':
		case 'start':
			icon = 'knk-align-left';
			break;

		case 'cc':
		case 'center':
			icon = 'knk-align-center';
			break;

		case 'ccc':
		case 'justify':
			icon = 'knk-align-justified';
			break;

		case 'cr':
		case 'right':
		case 'end':
			icon = 'knk-align-right';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const ObjectSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'cl':
			icon = 'knk-object-align-left';
			break;

		case 'cc':
			icon = 'knk-object-align-center';
			break;

		case 'cr':
			icon = 'knk-object-align-right';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const VideoOrigin = ({ position, change, value, ...props }) => {
	let icon = '';
	let tooltip = '';
	switch(position) {
		case 'youtube':
			icon = 'ep-youtube';
			tooltip = "YouTube";
			break;

		case 'vimeo':
			icon = 'ep-vimeo';
			tooltip = "Vimeo";
			break;

		case 'upload':
			icon = 'knk-upload';
			tooltip = "Upload";
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			tooltip={<Tooltip position="top" with_icon={false}>{tooltip}</Tooltip>}
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const RoleSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	let tooltip = '';
	switch(position) {
		case 'manager':
			icon = 'knk-home';
			tooltip = "Manager";
			break;

		case 'editor':
			icon = 'knk-edit';
			tooltip = "Editor";
			break;

		case 'designer':
			icon = 'knk-artist-mode';
			tooltip = "Designer";
			break;

		case 'analyst':
			icon = 'knk-stats';
			tooltip = "Analyst";
			break;

		case 'none':
			icon = 'knk-off redIcon';
			tooltip = "None";
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			tooltip={<Tooltip position="top" with_icon={false}>{tooltip}</Tooltip>}
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const ObjectSelectorV = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'tc':
			icon = 'knk-object-align-top';
			break;

		case 'cc':
			icon = 'knk-object-align-middle';
			break;

		case 'bc':
			icon = 'knk-object-align-bottom';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const OrientationSelector = ({ position, change, value, ...props }) => {
	let icon = '';
	switch(position) {
		case 'portrait':
			icon = 'knk-portrait-mode';
			break;

		case 'landscape':
			icon = 'knk-landscape-mode';
			break;
	}

	let classname = null;
	if(value == position) classname = { other:'selected' };

	return (
		<SquaredButton
			icon={icon}
			_class={classname}
			onClick={() => change(position)}
			aria-label="Change"
		/>
	);
};

const MatrixSelector = (props) => {
	const name = props?.name ?? false;
	const allow = props?.allow ?? 'xy';
	const _field_styles = props?._field_style ?? {};
	const _model = props?.model ?? {};
	const use_semantics = props?.use_semantics ?? false;
	const reset_enabled = props?.reset_enabled ?? false;

	let classname = props?.classname ?? 'input gui_matrix_selector';
	classname = parseClasses(classname, props._class, 'matrixselector');

	const only_center_edges = (allow == 'xy' && props.only_center_edges != undefined) ? props.only_center_edges : false;

	const value = props?.value || ((_model[name] != undefined) ? _model[name] : ((props._default_value) ? props._default_value : 'cc'));

	let matrix_class_arr = ['matrix_cont', 'field_container', `is_${allow}`];
	switch(allow) {
		case 'xy':
		case 'x':
		case 'y':
			break;

		default:
			matrix_class_arr.push('with_big_icons');
			break;
	}
	const matrix_class = matrix_class_arr.join(" ");

	const change = (position) => {
		if(props.change != undefined) props.change(name, position);
	};

	const enable_center = props?.enable_center ?? true;
	const enable_middle = props?.enable_middle ?? true;

	return (
		<div class={'react_field ' + (props?._field_class ?? '')} onMouseEnter={props.onMouseEnter} onMouseLeave={props.onMouseLeave} onClick={props.onClick} style={_field_styles}>
			<div class={classname}>
				{(props.children != undefined) && (
					<FieldLabel
						{...props.label_props}
						name={name}
						autotranslate={props.autotranslate}
					>{props.children}</FieldLabel>
				)}

				<div class={matrix_class}>
					{(allow == 'xy') && (
						<div class="matrix">
							<Selector
								position='tl'
								tooltip="Top-Left"
								only_center_edges={only_center_edges}
								change={change}
								value={value}
							/>

							<Selector
								position={(!only_center_edges) ? "tc" : "top"}
								tooltip={(!only_center_edges) ? "Top-Center" : "Top"}
								change={change}
								value={value}
							/>

							<Selector
								position='tr'
								tooltip="Top-Right"
								only_center_edges={only_center_edges}
								change={change}
								value={value}
							/>

							<Selector
								position={(!only_center_edges) ? "cl" : "left"}
								tooltip={(!only_center_edges) ? "Middle-Left" : "Left"}
								change={change}
								value={value}
							/>

							<Selector
								position='cc'
								tooltip="Middle-Center"
								only_center_edges={only_center_edges}
								change={change}
								value={value}
							/>

							<Selector
								position={(!only_center_edges) ? "cr" : "right"}
								tooltip={(!only_center_edges) ? "Middle-Right" : "Right"}
								change={change}
								value={value}
							/>

							<Selector
								position='bl'
								tooltip="Bottom-Left"
								only_center_edges={only_center_edges}
								change={change}
								value={value}
							/>

							<Selector
								position={(!only_center_edges) ? "bc" : "bottom"}
								tooltip={(!only_center_edges) ? "Bottom-Center" : "Bottom"}
								change={change}
								value={value}
							/>

							<Selector
								position='br'
								tooltip="Bottom-Right"
								only_center_edges={only_center_edges}
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'x') && (
						<div class="matrix">
							<Selector
								position='cl'
								tooltip="Left"
								change={change}
								value={value}
							/>

							{(enable_center) && (
								<Selector
									position='cc'
									tooltip="Center"
									change={change}
									value={value}
								/>
							)}

							<Selector
								position='cr'
								tooltip="Right"
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'y') && (
						<div class="matrix">
							<Selector
								position='tc'
								tooltip="Top"
								change={change}
								value={value}
							/>

							{(enable_middle) && (
								<Selector
									position='cc'
									tooltip="Middle"
									change={change}
									value={value}
								/>
							)}

							<Selector
								position='bc'
								tooltip="Bottom"
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'x_text') && (
						<div class="text_matrix">
							<TextSelector
								position={(use_semantics) ? 'start' : 'cl'}
								change={change}
								value={value}
							/>

							{(enable_center) && (
								<TextSelector
									position={(use_semantics) ? 'center' : 'cc'}
									change={change}
									value={value}
								/>
							)}

							{(props?.enable_justify ?? true) && (
								<TextSelector
									position={(use_semantics) ? 'justify' : 'ccc'}
									change={change}
									value={value}
								/>
							)}

							<TextSelector
								position={(use_semantics) ? 'end' : 'cr'}
								change={change}
								value={value}
							/>

							{(props.onReset != undefined) && (
								<SquaredButton
									icon='knk-off'
									color='red'
									disabled={(!reset_enabled) ? true : false}
									onClick={props.onReset}
									tooltip={<Tooltip position='top' with_icon={false}>Reset value</Tooltip>}
									aria-label="Reset value"
								/>
							)}
						</div>
					)}

					{(allow == 'x_text_weight') && (
						<div class="text_matrix">
							<TextWeightSelector
								position='p'
								change={change}
								value={value}
							/>

							<TextWeightSelector
								position='b'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'direction_math') && (
						<div class="text_matrix">
							<DirectionMathSelector
								position='more'
								change={change}
								value={value}
							/>

							<DirectionMathSelector
								position='less'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'photo_orientation') && (
						<div class="text_matrix">
							<PhotoOrientationSelector
								position='single'
								change={change}
								value={value}
							/>

							<PhotoOrientationSelector
								position='double'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'role') && (
						<div class="object_matrix">
							<RoleSelector
								position='manager'
								change={change}
								value={value}
							/>

							<RoleSelector
								position='editor'
								change={change}
								value={value}
							/>

							<RoleSelector
								position='designer'
								change={change}
								value={value}
							/>

							<RoleSelector
								position='analyst'
								change={change}
								value={value}
							/>

							<RoleSelector
								position='none'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'x_object') && (
						<div class="object_matrix">
							<ObjectSelector
								position='cl'
								change={change}
								value={value}
							/>

							{(enable_center) && (
								<ObjectSelector
									position='cc'
									change={change}
									value={value}
								/>
							)}

							<ObjectSelector
								position='cr'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'x_object_binary') && (
						<div class="object_matrix">
							<ObjectSelector
								position='cl'
								change={change}
								value={value}
							/>

							<ObjectSelector
								position='cr'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'y_object') && (
						<div class="object_matrix">
							<ObjectSelectorV
								position='tc'
								change={change}
								value={value}
							/>

							{(enable_middle) && (
								<ObjectSelectorV
									position='cc'
									change={change}
									value={value}
								/>
							)}

							<ObjectSelectorV
								position='bc'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'y_object_binary') && (
						<div class="object_matrix">
							<ObjectSelectorV
								position='tc'
								change={change}
								value={value}
							/>

							<ObjectSelectorV
								position='bc'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'orientation') && (
						<div class="object_matrix">
							<OrientationSelector
								position='portrait'
								change={change}
								value={value}
							/>

							<OrientationSelector
								position='landscape'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'rating_single_type') && (
						<div class="object_matrix">
							<RatingIcon
								position='ep-star'
								change={change}
								value={value}
							/>

							<RatingIcon
								position='ep-heart'
								change={change}
								value={value}
							/>

							<RatingIcon
								position='ep-circle'
								change={change}
								value={value}
							/>

							<RatingIcon
								position='ep-thumbs-up'
								change={change}
								value={value}
							/>

							<RatingIcon
								position='ep-thumbs-down'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'video_origin') && (
						<div class="object_matrix">
							<VideoOrigin
								position='upload'
								change={change}
								value={value}
							/>

							<VideoOrigin
								position='vimeo'
								change={change}
								value={value}
							/>

							<VideoOrigin
								position='youtube'
								change={change}
								value={value}
							/>
						</div>
					)}

					{(allow == 'custom') && (
						<div class="object_matrix">
							{props.items.map((item, i) => {
								return (
									<CustomSelector
										key={`item_${i}`}
										item={item}
										change={change}
										value={value}
									/>
								)
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default injectIntl(MatrixSelector);